import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { fmtCurrency } from "../lib/formatters";
import { useElementSeries } from "../lib/useElementSeries";

// Step 13 · Chart 1 → sub-view · Full Funding Calculation (Dave 2026-08-05)
//
// Element-by-element × year-by-year table showing accumulated depreciation.
// Series are server-computed (POST /associations/{aid}/element-series).
//
// Column layout: 4 sticky-left columns (Element / UL / RL / Study-year cost)
// + 2 columns per horizon year (year total, %Dep). The whole row scrolls
// horizontally beyond the sticky prefix.

// Palette
const INK = "#1A1A1A";
const NAVY = "#1C4E80";
const CORAL = "#EA6A47";
const POST_REP_BLUE = "#3B82C4";
const CELL_BORDER = "#E2E8F0";
const HEADER_BG = "#1C4E80";

function fmtPctInt(v) {
  return `${Math.round(v)}%`;
}

// Truncation-aware text — only surfaces the `title` (native tooltip)
// when the rendered text actually overflows its container. Prevents
// noisy tooltips on rows whose labels already fit fully.
function TruncatedText({ text, className, style, testid }) {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    check();
    // Recheck on window resize so column-width changes are picked up.
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [text]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...(style || {}), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      title={isTruncated ? text : undefined}
      data-testid={testid}
    >
      {text}
    </div>
  );
}

export default function FullFundingCalculationTable({
  calc,
  variables,
  association,
  horizon,
}) {
  const inflation = variables?.inflation_rate || 0;
  const studyYear = calc?.study_year || 2026;
  const elements = association?.elements || [];

  // Server-computed series (POST /element-series); browser only formats.
  const series = useElementSeries(association?.aid, variables);
  const { rows, ffbByYear, sumCurrentCost } = useMemo(() => {
    const byId = Object.fromEntries(elements.map((e) => [e.element_id, e]));
    const rowsData = (series?.ffb_elements || [])
      .filter((r) => byId[r.element_id])
      .map((r) => {
        const element = byId[r.element_id];
        const firstRep = r.replacement_years.length ? r.replacement_years[0] : Infinity;
        const years = r.amounts.slice(0, horizon).map((amount, i) => {
          const isReplacement = r.replacement_years.includes(i + 1);
          return { amount, pctDep: r.pct_dep[i], isReplacement, isPostRep: !isReplacement && i + 1 > firstRep };
        });
        return { el: element, display: element.element_display_id || element.element_id, row: { years } };
      });
    return {
      rows: rowsData,
      ffbByYear: (series?.ffb_by_year || []).slice(0, horizon),
      sumCurrentCost: series?.ffb_total_cost || 0,
    };
  }, [series, elements, horizon]);

  const horizonYears = Array.from({ length: horizon }, (_, i) => studyYear + i);
  const inflationPct = (inflation * 100).toFixed(0);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="full-funding-calculation-view">
      {/* Sub-header line */}
      <div className="px-5 pt-3 pb-2 flex items-baseline gap-3">
        <div className="text-[15px] font-bold text-navy">Full Funding Calculation</div>
        <div className="text-[11px] text-slate">
          {horizon}-Year Horizon <span className="mx-1 text-slate/60">|</span>
          Inflation: {inflationPct}% <span className="mx-1 text-slate/60">|</span>
          {rows.length} Elements
        </div>
      </div>

      {/* Scrolling table container */}
      <div
        className="flex-1 min-h-0 overflow-auto border-t border-b border-rule"
        data-testid="ffc-scroll"
        style={{ background: "#fff" }}
      >
        <table
          className="border-collapse text-[11px]"
          style={{ fontFamily: "IBM Plex Mono, monospace", minWidth: "max-content" }}
        >
          <thead>
            <tr style={{ background: HEADER_BG, color: "#fff" }}>
              <Th sticky left={0} minW={210} align="left" pad="8px 10px" data-testid="ffc-hdr-element">
                Element
              </Th>
              <Th sticky left={210} minW={44} pad="8px 6px">UL</Th>
              <Th sticky left={254} minW={44} pad="8px 6px">RL</Th>
              <Th sticky left={298} minW={104} align="right" pad="8px 10px" borderRight={`3px solid ${CORAL}`}>
                {studyYear - 1} Cost
              </Th>
              {horizonYears.map((yr) => (
                <React.Fragment key={yr}>
                  <Th minW={84} align="right" pad="8px 8px">{yr}</Th>
                  <Th minW={54} align="right" pad="8px 8px">% Dep</Th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ el, display, row }, rIdx) => (
              <tr key={el.element_id} style={{ background: rIdx % 2 === 0 ? "#fff" : "#F8FAFC" }}>
                <Td sticky left={0} minW={210} bg={rIdx % 2 === 0 ? "#fff" : "#F8FAFC"} align="left" pad="6px 10px" data-testid={`ffc-row-${display}`}>
                  <TruncatedText
                    text={el.name}
                    className="font-semibold text-navy leading-tight"
                    style={{ maxWidth: 200 }}
                    testid={`ffc-row-${display}-name`}
                  />
                </Td>
                <Td sticky left={210} minW={44} bg={rIdx % 2 === 0 ? "#fff" : "#F8FAFC"} pad="6px 6px">
                  {el.useful_life_when_new}
                </Td>
                <Td sticky left={254} minW={44} bg={rIdx % 2 === 0 ? "#fff" : "#F8FAFC"} pad="6px 6px">
                  {el.remaining_useful_life}
                </Td>
                <Td
                  sticky
                  left={298}
                  minW={104}
                  bg={rIdx % 2 === 0 ? "#fff" : "#F8FAFC"}
                  align="right"
                  pad="6px 10px"
                  borderRight={`3px solid ${CORAL}`}
                >
                  {fmtCurrency(el.extended_cost)}
                </Td>
                {row.years.map((y, idx) => {
                  const color = y.isReplacement ? CORAL : y.isPostRep ? POST_REP_BLUE : INK;
                  return (
                    <React.Fragment key={idx}>
                      <Td
                        minW={84}
                        align="right"
                        pad="6px 8px"
                        style={{ color, fontWeight: y.isReplacement ? 700 : 500 }}
                      >
                        {fmtCurrency(y.amount)}
                        {y.isReplacement && <span style={{ marginLeft: 4 }}>R</span>}
                      </Td>
                      <Td minW={54} align="right" pad="6px 8px" style={{ color }}>
                        {fmtPctInt(y.pctDep)}
                      </Td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}

            {/* FFB totals row — every cell is individually sticky-bottom so
                the whole row stays pinned even when a parent cell asserts
                its own sticky-left context (which overrides an ancestor
                `<tr>` sticky). Sticky-left prefix cells layer above the
                sticky-bottom-only year cells via z-index (see `Td`). */}
            <tr style={{ background: NAVY, color: "#fff" }} data-testid="ffc-ffb-row">
              <Td sticky stickyBottom left={0} minW={210} bg={NAVY} color="#fff" align="left" pad="8px 10px" bold>
                Full Funding Balance (FFB)
              </Td>
              <Td sticky stickyBottom left={210} minW={44} bg={NAVY} color="#fff" pad="8px 6px" />
              <Td sticky stickyBottom left={254} minW={44} bg={NAVY} color="#fff" pad="8px 6px" />
              <Td
                sticky
                stickyBottom
                left={298}
                minW={104}
                bg={NAVY}
                color="#fff"
                align="right"
                pad="8px 10px"
                bold
                borderRight={`3px solid ${CORAL}`}
              >
                {fmtCurrency(sumCurrentCost)}
              </Td>
              {ffbByYear.map((v, idx) => (
                <React.Fragment key={idx}>
                  <Td stickyBottom minW={84} bg={NAVY} color="#fff" align="right" pad="8px 8px" bold>
                    {fmtCurrency(v)}
                  </Td>
                  <Td stickyBottom minW={54} bg={NAVY} color="#fff" pad="8px 8px" />
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Bottom legend */}
      <div
        className="flex items-center justify-between px-5 py-2 text-[11px] text-slate"
        data-testid="ffc-legend"
        style={{ background: "#F7FAFC" }}
      >
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5">
            <span style={{ color: POST_REP_BLUE, fontWeight: 700 }}>Blue</span>
            = post-replacement cycle
          </span>
          <span className="text-slate/60">|</span>
          <span className="inline-flex items-center gap-1.5">
            <span style={{ color: CORAL, fontWeight: 700 }}>R</span>
            = replacement year
          </span>
        </div>
        <div className="text-slate">
          UL = Useful Life <span className="mx-1 text-slate/60">|</span>
          RL = Remaining Life <span className="mx-1 text-slate/60">|</span>
          Acc. Depr = Accumulated Depreciation
        </div>
      </div>
    </div>
  );
}

// ─── tiny primitives ─────────────────────────────────────────────────────
function Th({ children, sticky, left, minW, align = "center", pad, borderRight, ...rest }) {
  const style = {
    position: sticky ? "sticky" : undefined,
    left: sticky ? left : undefined,
    minWidth: minW,
    width: minW,
    padding: pad,
    textAlign: align,
    borderRight: borderRight || `1px solid ${CELL_BORDER}`,
    background: sticky ? HEADER_BG : undefined,
    zIndex: sticky ? 2 : 1,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };
  return (
    <th style={style} {...rest}>
      {children}
    </th>
  );
}
function Td({
  children,
  sticky,
  stickyBottom,
  left,
  minW,
  align = "center",
  pad,
  bg,
  color,
  bold,
  borderRight,
  style: extraStyle,
  ...rest
}) {
  // z-index layering when the sticky-left prefix meets the sticky-bottom
  // totals row: the intersection cells (both axes sticky) must sit on
  // top of both sticky-left body cells and sticky-bottom year cells so
  // nothing bleeds through at the corner.
  //   sticky-left + sticky-bottom → 3 (totals prefix intersection)
  //   sticky-bottom only           → 2 (totals year cells)
  //   sticky-left only             → 1 (body row prefix)
  //   default                      → 0
  let z = 0;
  if (sticky && stickyBottom) z = 3;
  else if (stickyBottom) z = 2;
  else if (sticky) z = 1;

  const style = {
    position: sticky || stickyBottom ? "sticky" : undefined,
    left: sticky ? left : undefined,
    bottom: stickyBottom ? 0 : undefined,
    minWidth: minW,
    width: minW,
    padding: pad,
    textAlign: align,
    borderRight: borderRight || `1px solid ${CELL_BORDER}`,
    borderBottom: `1px solid ${CELL_BORDER}`,
    background: bg,
    color,
    fontWeight: bold ? 700 : undefined,
    whiteSpace: "nowrap",
    zIndex: z,
    ...(extraStyle || {}),
  };
  return (
    <td style={style} {...rest}>
      {children}
    </td>
  );
}
